import { useEffect } from 'react';

import { QuestionPickerTheme } from 'pages/custom_tests_v1/components/question_picker/components/QuestionPickerTheme';
import { QuestionPickerContext } from 'pages/custom_tests_v1/components/question_picker/context';
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap';
import { FAIcon } from 'components/fontawesome_icon/FAIcon';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';

// === Question Tree (JSON representation of XML question hierarchy) ===

export type QuestionBook = {
    BookId: number;
    BookName: string;
    Pages: number[];
    PublisherName: string;
};

export type QuestionItem = {
    Books: QuestionBook[];
    IsChecked: boolean;
    Name: string;
    QuestionId: number;
};

export type QuestionObjective = {
    Name: string;
    ObjectiveId: number;
    Questions: QuestionItem[];
};

export type QuestionTheme = {
    Name: string;
    Objectives: QuestionObjective[];
    ThemeId: number;
};

export type QuestionProduct = {
    Books: QuestionBook[];
    NumSelectedQuestions: number;
    ProductId: number;
    ProductName: string;
    Themes: QuestionTheme[];
};

export type QuestionTree = {
    Products: QuestionProduct[];
};

export type QuestionTreeResult = QuestionTree & {
    XmlString: string;
};

export type QuestionPickerContextValue = {
    deselectAllInObjective: (objective: QuestionObjective) => void;
    deselectAllInTheme: (theme: QuestionTheme) => void;
    getFilteredObjectives: (objectives: QuestionObjective[]) => QuestionObjective[];
    getThemeSelectedCount: (theme: QuestionTheme) => number;
    getThemeTotalCount: (theme: QuestionTheme) => number;
    resetFilters: () => void;
    selectAllInObjective: (objective: QuestionObjective) => void;
    selectAllInTheme: (theme: QuestionTheme) => void;
    selectedQuestionIds: Set<number>;
    toggleQuestion: (questionId: number) => void;
};

type QuestionPickerProps = {
    loading: boolean;
    onChange: (questionIds: string, totalSelected: number) => void;
    questionTree: QuestionTree;
    selectedIds?: string;
};

export const QuestionPicker = (props: QuestionPickerProps) => {

    const { loading, onChange, questionTree, selectedIds } = props;

    const [state, setState] = useSuperState({
        bookFilter: '',
        objectiveFilter: '',
        pageRangeFilter: '',
        selectedQuestionIds: new Set<number>()
    });

    const { formatMessage } = useIntl();

    useEffect(() => {

        const selectedSet = new Set(
            (selectedIds ?? '').split(',').map(s => s.trim()).filter(Boolean).map(Number)
        );

        const initialSelected = new Set<number>();

        questionTree.Products?.forEach(product => {

            product.Themes?.forEach(theme => {

                theme.Objectives?.forEach(objective => {

                    objective.Questions?.forEach(question => {

                        if (selectedSet.has(question.QuestionId) || question.IsChecked) {

                            initialSelected.add(question.QuestionId);

                        }

                    });

                });

            });

        });

        setState({ selectedQuestionIds: initialSelected });

    }, [questionTree, selectedIds]);

    useEffect(() => {

        const ids = Array.from(state.selectedQuestionIds).join(',');
        onChange(ids, state.selectedQuestionIds.size);

    }, [state.selectedQuestionIds]);

    const resetFilters = () => setState({
        bookFilter: '',
        objectiveFilter: '',
        pageRangeFilter: ''
    });

    const parsePageRanges = (rangeStr: string): [number, number][] => rangeStr.split(',').flatMap(part => {

        const match = part.trim().match(/^(\d+)(?:-(\d+))?$/);
        if (!match) return [];
        const start = parseInt(match[1], 10);
        const end = match[2] ? parseInt(match[2], 10) : start;
        return [[start, end] as [number, number]];

    });

    const questionMatchesFilters = (question: QuestionItem): boolean => {

        if (state.bookFilter) {

            const hasBook = question.Books?.some(b => String(b.BookId) === state.bookFilter);
            if (!hasBook) return false;

        }

        if (state.pageRangeFilter.trim()) {

            const ranges = parsePageRanges(state.pageRangeFilter);
            if (ranges.length > 0) {

                const allPages = question.Books?.flatMap(b => b.Pages ?? []) ?? [];
                const inRange = allPages.some(pg => ranges.some(([lo, hi]) => pg >= lo && pg <= hi));
                if (!inRange) return false;

            }

        }

        return true;

    };

    const getFilteredObjectives = (objectives: QuestionObjective[]): QuestionObjective[] => objectives
        .filter(o => !state.objectiveFilter || String(o.ObjectiveId) === state.objectiveFilter)
        .map(o => ({ ...o, Questions: o.Questions?.filter(questionMatchesFilters) ?? [] }))
        .filter(o => o.Questions.length > 0);

    const toggleQuestion = (questionId: number) => {

        const updated = new Set(state.selectedQuestionIds);
        if (updated.has(questionId)) {

            updated.delete(questionId);

        } else {

            updated.add(questionId);

        }
        setState({ selectedQuestionIds: updated });

    };

    const selectAllInTheme = (theme: QuestionTheme) => {

        const updated = new Set(state.selectedQuestionIds);
        theme.Objectives?.forEach(o => o.Questions?.forEach(q => updated.add(q.QuestionId)));
        setState({ selectedQuestionIds: updated });

    };

    const deselectAllInTheme = (theme: QuestionTheme) => {

        const updated = new Set(state.selectedQuestionIds);
        theme.Objectives?.forEach(o => o.Questions?.forEach(q => updated.delete(q.QuestionId)));
        setState({ selectedQuestionIds: updated });

    };

    const selectAllInObjective = (objective: QuestionObjective) => {

        const updated = new Set(state.selectedQuestionIds);
        objective.Questions?.forEach(q => updated.add(q.QuestionId));
        setState({ selectedQuestionIds: updated });

    };

    const deselectAllInObjective = (objective: QuestionObjective) => {

        const updated = new Set(state.selectedQuestionIds);
        objective.Questions?.forEach(q => updated.delete(q.QuestionId));
        setState({ selectedQuestionIds: updated });

    };

    const getThemeSelectedCount = (theme: QuestionTheme): number => {

        let count = 0;
        theme.Objectives?.forEach(o => o.Questions?.forEach(q => {

            if (state.selectedQuestionIds.has(q.QuestionId)) count++;

        }));
        return count;

    };

    const getThemeTotalCount = (theme: QuestionTheme): number => {

        let count = 0;
        theme.Objectives?.forEach(o => count += o.Questions?.length ?? 0);

        return count;

    };

    const uniqueBooks: QuestionBook[] = Array.from(
        new Map((questionTree.Products?.flatMap(p => p.Books ?? []) ?? [])
            .map(b => [b.BookId, b]))
            .values()
    );

    const context: QuestionPickerContextValue = {
        deselectAllInObjective,
        deselectAllInTheme,
        getFilteredObjectives,
        getThemeSelectedCount,
        getThemeTotalCount,
        resetFilters,
        selectAllInObjective,
        selectAllInTheme,
        selectedQuestionIds: state.selectedQuestionIds,
        toggleQuestion
    };

    return (
        <QuestionPickerContext.Provider value={context}>
            <div className="py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                        <FormattedMessage defaultMessage="Select Questions" id="select_questions.9f4f31f1" />
                    </h5>
                    <span className="badge badge-primary">
                        {state.selectedQuestionIds.size}
                        {' '}
                        <FormattedMessage defaultMessage="questions selected" id="questions_selected.11c36841" />
                    </span>
                </div>

                <Row className="mb-3 gy-3">
                    <Col md={3}>
                        <Form.Select
                            onChange={e => setState({ objectiveFilter: e.target.value })}
                            size={'sm'}
                            value={state.objectiveFilter}
                        >
                            <option value="">
                                {formatMessage({ id: 'all_objectives.8326d1e1', defaultMessage: 'All Objectives' })}
                            </option>

                            {questionTree.Products?.flatMap(product => (product.Themes ?? [])
                                .filter(theme => (theme.Objectives?.length ?? 0) > 0)
                                .map(theme => (
                                    <optgroup
                                        label={(questionTree.Products?.length ?? 0) > 1
                                            ? `${product.ProductName} — ${theme.Name}`
                                            : theme.Name
                                        }
                                        key={`${product.ProductId}-${theme.ThemeId}`}
                                    >
                                        {theme.Objectives?.map(o => (
                                            <option key={`${product.ProductId}-${theme.ThemeId}-${o.ObjectiveId}`} value={String(o.ObjectiveId)}>
                                                {o.Name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))
                            )}

                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            onChange={e => setState({ bookFilter: e.target.value })}
                            size={'sm'}
                            value={state.bookFilter}
                        >
                            <option value="">
                                {formatMessage({ id: 'all_books.64ace5dc', defaultMessage: 'All Books' })}
                            </option>
                            {uniqueBooks.map(b => (
                                <option key={b.BookId} value={String(b.BookId)}>
                                    {b.BookName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Control
                            onChange={e => setState({ pageRangeFilter: e.target.value })}
                            placeholder={formatMessage({ id: 'page_range_eg_1_15100_180.e84c6bd7', defaultMessage: 'Page range (e.g. 1-15,100-180)' })}
                            size={'sm'}
                            type="text"
                            value={state.pageRangeFilter}
                        />
                    </Col>
                    <Col>
                        <Button
                            className={'d-flex align-items-center gap-2'}
                            onClick={resetFilters}
                            size={'sm'}
                            variant={'outline-primary'}
                        >
                            <span>
                                <FormattedMessage defaultMessage="Reset filters" id="reset_filters.87ad62a1" />
                            </span>
                            <FAIcon library={'regular'} name={'undo'} />
                        </Button>
                    </Col>
                </Row>

                {loading ?
                    (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">
                                    <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                                </span>
                            </div>
                        </div>
                    )
                    :
                    (
                        <div>

                            {questionTree.Products?.map(product => (
                                <div className="mb-3" key={product.ProductId}>
                                    <h6 className="text-primary mb-2">
                                        {product.ProductName}
                                    </h6>
                                    <Accordion alwaysOpen>
                                        {product.Themes?.map(theme => (
                                            <QuestionPickerTheme
                                                key={`${product.ProductId}-${theme.ThemeId}`}
                                                productId={product.ProductId}
                                                theme={theme}
                                            />
                                        ))}
                                    </Accordion>
                                </div>
                            ))}

                            {(!questionTree.Products || questionTree.Products.length === 0) && (
                                <div className="text-center text-muted py-4">
                                    <FormattedMessage defaultMessage="No questions available. Please select products first." id="no_questions_available_please_select_pro.416aa3f7" />
                                </div>
                            )}
                        </div>
                    )
                }
            </div>
        </QuestionPickerContext.Provider>
    );

};
